import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Trash2, Plus } from "lucide-react";

export const bomKey = (r) => `${r.material_id}${r.fused ? ":ad" : ""}`;

const hay = (m) => [m.name, m.fabric_type, m.color, m.supplier, m.supplier_code, m.type].filter(Boolean).join(" ").toLowerCase();
const variantOf = (m) => [m.color, m.supplier].filter(Boolean).join(" · ");

const Result = ({ m, fused, onAdd, added }) => (
  <button type="button" disabled={added} onClick={() => onAdd(m, fused)} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-secondary/50 disabled:opacity-40" data-testid={`bom-add-${m.id}${fused ? "-ad" : ""}`}>
    <span><span className="font-medium">{m.name}</span>{fused && <span className="ml-1.5 rounded bg-sky-500/15 px-1 text-[10px] text-sky-400">adesivato</span>}
      <span className="block text-[11px] text-muted-foreground">{[m.fabric_type, variantOf(m)].filter(Boolean).join(" · ")}</span></span>
    {added ? <span className="text-[11px]">aggiunto</span> : <Plus className="h-4 w-4 shrink-0" />}
  </button>
);

export const BomEditor = ({ materials, rows, setRows }) => {
  const [q, setQ] = useState("");
  const keys = new Set(rows.map(bomKey));
  const found = useMemo(() => {
    const w = q.toLowerCase().split(/\s+/).filter(Boolean);
    return w.length ? materials.filter((m) => w.every((x) => hay(m).includes(x))).slice(0, 30) : [];
  }, [q, materials]);
  const add = (m, fused) => setRows([...rows, { material_id: m.id, fused, material_name: m.name, variant: variantOf(m), unit: m.unit, qpu: "" }]);
  return (
    <div className="space-y-3 py-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca tessuto o accessorio: nome, tipo, colore, fornitore..." className="pl-9" data-testid="bom-search" />
      </div>
      {q && (
        <div className="max-h-56 divide-y divide-border overflow-y-auto rounded-md border border-border" data-testid="bom-results">
          {found.flatMap((m) => [
            <Result key={m.id} m={m} fused={false} onAdd={add} added={keys.has(m.id)} />,
            m.fusible ? <Result key={`${m.id}-ad`} m={m} fused onAdd={add} added={keys.has(`${m.id}:ad`)} /> : null,
          ])}
          {!found.length && <p className="p-3 text-xs text-muted-foreground">Nessun materiale trovato.</p>}
        </div>
      )}
      <div className="space-y-2" data-testid="bom-rows">
        {rows.map((r, i) => (
          <div key={bomKey(r)} className="flex items-center gap-3" data-testid={`bom-row-${bomKey(r)}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{r.material_name}{r.fused && <span className="ml-1.5 rounded bg-sky-500/15 px-1 text-[10px] text-sky-400">adesivato</span>}</p>
              <p className="text-[11px] text-muted-foreground">{r.variant ? `${r.variant} · ` : ""}per capo · {r.unit}</p>
            </div>
            <Input type="number" className="w-24" value={r.qpu} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, qpu: e.target.value } : x)))} data-testid={`bom-qpu-${bomKey(r)}`} />
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => setRows(rows.filter((_, j) => j !== i))} data-testid={`bom-remove-${bomKey(r)}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
        {!rows.length && <p className="rounded-md border border-dashed border-border p-3 text-center text-xs text-muted-foreground">Nessun materiale nella distinta: cercalo qui sopra e aggiungilo.</p>}
      </div>
    </div>
  );
};
